import React, { useState, useRef } from 'react';
import { transformations } from '../data/mock';

const GallerySection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showHint, setShowHint] = useState(true);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const activeTransformation = transformations[activeTab];

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
    setShowHint(false);
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };

  const handleTouchStart = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Smile Gallery
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            Real transformations from our patients - Hear their stories
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Tabs - Grid layout matching original */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-1 bg-gray-100 p-1 rounded-lg mb-8">
            {transformations.map((transformation, index) => (
              <button
                key={transformation.id}
                onClick={() => {
                  setActiveTab(index);
                  setSliderPosition(50);
                  setShowHint(true);
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === index
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {transformation.name}
              </button>
            ))}
          </div>

          {/* Before/After Comparison Slider */}
          <div className="space-y-8">
            <div className="max-w-4xl mx-auto">
              <div
                ref={containerRef}
                className="relative w-full h-[500px] overflow-hidden rounded-lg select-none cursor-ew-resize shadow-lg"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
              >
                {/* After Image (Background) */}
                <div className="absolute inset-0">
                  <img
                    src={activeTransformation.afterImage}
                    alt={`After dental treatment - ${activeTransformation.name}`}
                    className="w-full h-full object-cover object-[center_75%]"
                    draggable="false"
                  />
                  <div className="absolute bottom-4 right-4 bg-cyan-600/90 text-white px-4 py-2 rounded-full font-semibold text-sm backdrop-blur-sm">
                    After
                  </div>
                </div>

                {/* Before Image (Clipped) */}
                <div
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img
                    src={activeTransformation.beforeImage}
                    alt={`Before dental treatment - ${activeTransformation.name}`}
                    className="w-full h-full object-cover object-[center_75%]"
                    draggable="false"
                  />
                  <div className="absolute bottom-4 left-4 bg-gray-700/90 text-white px-4 py-2 rounded-full font-semibold text-sm backdrop-blur-sm">
                    Before
                  </div>
                </div>

                {/* Slider Handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-cyan-600">
                    <div className="flex gap-1">
                      <div className="w-0.5 h-4 bg-cyan-600 rounded" />
                      <div className="w-0.5 h-4 bg-cyan-600 rounded" />
                    </div>
                  </div>
                  {/* Arrow indicators */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 pointer-events-none">
                    <svg 
                      className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full text-white drop-shadow-lg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16" 
                      fill="currentColor"
                    >
                      <path d="M11 8l-5-5v10l5-5z" />
                    </svg>
                    <svg 
                      className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full text-white drop-shadow-lg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 16 16" 
                      fill="currentColor"
                    >
                      <path d="M5 8l5-5v10l-5-5z" />
                    </svg>
                  </div>
                </div>

                {/* Drag Hint Overlay */}
                {showHint && (
                  <div className="absolute inset-0 bg-black/20 pointer-events-none flex items-center justify-center">
                    <div className="bg-white/95 text-gray-900 px-6 py-3 rounded-full text-sm font-medium shadow-lg backdrop-blur-sm">
                      ← Drag to compare →
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-8 max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed">
                {activeTransformation.description}
              </p>
            </div>

            {/* Gallery CTA */}
            <div className="mt-8 text-center" data-testid="gallery-cta">
              <p className="text-gray-500 text-sm mb-3">Want results like these?</p>
              <a
                href="#sleep-apnea"
                className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                Request Free Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
